import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    // Check if desktop (not mobile/tablet)
    const isDesktopDevice = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
      window.matchMedia('(min-width: 1024px)').matches;
    setIsDesktop(isDesktopDevice);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ PWA installation accepted');
        setIsInstalled(true);
      } else {
        console.log('❌ PWA installation dismissed');
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error installing PWA:', error);
    }
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Don't show info on desktop web (only show on mobile/tablet)
  if (isDesktop) {
    return null;
  }

  // iOS instructions
  if (isIOS) {
    return (
      <div className="w-full p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg border border-pink-200">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📱</div>
          <div className="flex-1">
            <h3 className="font-semibold text-pink-900 mb-1">Installa l'App YouPass</h3>
            <p className="text-sm text-pink-700 mb-3">
              Per un'esperienza completa, installa la nostra Progressive Web App. Riceverai notifiche push quando i tuoi voucher vengono riscattati!
            </p>
            <button
              onClick={() => {
                // Show instructions for iOS
                alert('Per installare l\'app su iOS:\n\n1. Tocca il pulsante Condividi (icona quadrato con freccia)\n2. Seleziona "Aggiungi alla schermata Home"\n3. Tocca "Aggiungi"');
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95 touch-manipulation"
            >
              <span>📱</span>
              <span>Mostra Istruzioni</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop - show install button if prompt is available
  if (deferredPrompt) {
    return (
      <div className="w-full p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg border border-pink-200">
        <div className="flex items-start gap-3">
          <div className="text-2xl">📱</div>
          <div className="flex-1">
            <h3 className="font-semibold text-pink-900 mb-1">Installa l'App YouPass</h3>
            <p className="text-sm text-pink-700 mb-3">
              Per un'esperienza completa, installa la nostra Progressive Web App. Riceverai notifiche push quando i tuoi voucher vengono riscattati!
            </p>
            <button
              onClick={handleInstallClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95 touch-manipulation"
            >
              <span>📱</span>
              <span>Installa App</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No prompt available yet (Android/Chrome - might need to visit multiple times)
  return (
    <div className="w-full p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg border border-pink-200">
      <div className="flex items-start gap-3">
        <div className="text-2xl">📱</div>
        <div className="flex-1">
          <h3 className="font-semibold text-pink-900 mb-1">Installa l'App YouPass</h3>
          <p className="text-sm text-pink-700 mb-2">
            Per un'esperienza completa, installa la nostra Progressive Web App. Riceverai notifiche push quando i tuoi voucher vengono riscattati!
          </p>
          <p className="text-xs text-pink-600">
            💡 Su Android: apri il menu (⋮) e seleziona "Installa app" o "Aggiungi alla schermata Home"
          </p>
        </div>
      </div>
    </div>
  );
}
